import { Meh, XCircle } from 'lucide-react';
import { ModalContent } from '@sandtech-hub-monorepo/ui-kit';
import {
  AppealStatus,
  ConductCaseCategory,
  ConductCaseSeverity,
} from '@sandtech-hub-monorepo/ehub-client';
import { formatDate } from '@sandtech-hub-monorepo/ui-kit-site';
import { ReactComponent as StarsIcon } from '../../../../assets/Icons/stars.svg';
import { SeverityRow } from './SeverityRow';

interface Props {
  appealStatus: AppealStatus;
  appealSubmittedAt: string;
  messageFromAdmin: string | null;
  probationStatus?: string | null;
  appealResolvedAt?: string | null;
  category?: ConductCaseCategory | null;
  previousSeverity?: ConductCaseSeverity | null;
  newSeverity?: ConductCaseSeverity | null;
}

export const AppealDetailModal = ({
  appealStatus,
  appealSubmittedAt,
  messageFromAdmin,
  probationStatus,
  appealResolvedAt,
  category,
  previousSeverity,
  newSeverity,
}: Props) => {
  const isPending = appealStatus === AppealStatus.Pending;
  const isDenied = appealStatus === AppealStatus.Denied;

  const formattedDate = appealSubmittedAt ? formatDate(appealSubmittedAt) : null;

  const formattedResolvedDate = appealResolvedAt
    ? formatDate(appealResolvedAt)
    : null;

  if (isPending) {
    return (
      <ModalContent>
        <div className="flex flex-col items-center max-w-[500px] mx-auto py-2">
          <div className="p-5 bg-info/20 rounded-full text-info mb-4">
            <Meh height="48" width="48" />
          </div>
          <h2 className="text-info font-semibold text-xl mb-3 text-center">
            Appeal Under Review
          </h2>
          <p className="text-muted-foreground text-sm mb-6 text-center">
            Your academic appeal has been submitted and is currently under
            review by the Academic Standing Committee.
          </p>
          <div className="w-full border-y border-muted py-3 mb-6">
            <p className="text-sm font-semibold text-foreground text-center">
              Appeal Details
            </p>
          </div>
          <div className="w-full grid grid-cols-2 gap-x-4 gap-y-6">
            {formattedDate && (
              <div className="flex flex-col gap-1">
                <p className="text-sm text-muted-foreground">Submitted Date</p>
                <p className="text-sm font-semibold text-foreground">
                  {formattedDate}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">
                Expected Review Period
              </p>
              <p className="text-sm font-semibold text-foreground">
                3-5 Business Days
              </p>
            </div>
            {probationStatus && (
              <div className="flex flex-col gap-1">
                <p className="text-sm text-muted-foreground">
                  Probation Status
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {probationStatus}
                </p>
              </div>
            )}
          </div>
        </div>
      </ModalContent>
    );
  }

  if (isDenied) {
    return (
      <ModalContent>
        <div className="flex flex-col max-w-[500px] mx-auto py-2">
          <div className="flex flex-col items-center">
            <div className="p-5 bg-destructive/10 rounded-full text-destructive mb-4">
              <XCircle height="48" width="48" />
            </div>
            <h2 className="text-destructive font-semibold text-2xl mb-3 text-center">
              Appeal Unsuccessful
            </h2>
            <p className="text-muted-foreground text-sm mb-6 text-center">
              Unfortunately, your academic appeal has been reviewed and was not
              approved by the Academic Standing Committee.
            </p>
          </div>
          {formattedResolvedDate && (
            <div className="flex items-center gap-3 mb-6 text-sm">
              <span className="text-muted-foreground">Appeal Declined:</span>
              <span className="font-semibold text-foreground">
                {formattedResolvedDate}
              </span>
            </div>
          )}
          {messageFromAdmin && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-foreground">
                Appeal notes
              </p>
              <div className="bg-info/10 rounded-lg p-4">
                <p className="text-sm italic text-info">{messageFromAdmin}</p>
              </div>
            </div>
          )}
        </div>
      </ModalContent>
    );
  }

  return (
    <ModalContent>
      <div className="flex flex-col max-w-[500px] mx-auto py-2 gap-4">
        <div className="flex flex-col items-center gap-4">
          <div className="p-6 bg-success/10 rounded-full text-success">
            <StarsIcon height="38" width="38" />
          </div>
          <h2 className="text-success font-semibold text-2xl text-center">
            Appeal Successful!
          </h2>
          <p className="text-muted-foreground text-sm text-center">
            Congratulations! Your academic appeal has been reviewed and approved
            by the Academic Standing Committee.
          </p>
        </div>

        {formattedResolvedDate && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">Appeal Approved:</span>
            <span className="font-semibold text-foreground">
              {formattedResolvedDate}
            </span>
          </div>
        )}

        {messageFromAdmin && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-foreground">Appeal notes</p>
            <div className="bg-info/10 rounded-lg p-4">
              <p className="text-sm italic text-info">{messageFromAdmin}</p>
            </div>
          </div>
        )}

        {previousSeverity && newSeverity && category && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-foreground">
              Updated severity
            </p>
            <SeverityRow
              label="From"
              severity={previousSeverity}
              category={category}
            />
            <SeverityRow
              label="To"
              severity={newSeverity}
              category={category}
            />
          </div>
        )}
      </div>
    </ModalContent>
  );
};
